import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SparePart } from '../../types';
import { apiClient } from '../../services/apiClient';
import './SelectionModal.css';

interface SparePartSelectionModalProps {
  onSelect: (sparePart: SparePart) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const SparePartSelectionModal: React.FC<SparePartSelectionModalProps> = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  const [spareParts, setSpareParts] = useState<SparePart[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const searchSpareParts = useCallback(async (query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setSpareParts([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.searchSparePartsCatalog(query);
      setSpareParts(data);
      setHasSearched(true);
    } catch (err) {
      setError(t('messages.errorLoadingData'));
      setSpareParts([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      searchSpareParts(searchQuery);
    }, DEBOUNCE_MS);

    // Cleanup on unmount or query change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, searchSpareParts]);

  const handleSelect = (sparePart: SparePart) => {
    onSelect(sparePart);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('sparePartsForm.addSparePart')}</h3>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="modal-body">
          <input
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={handleSearchChange}
            className="filter-input"
            autoFocus
          />

          <div className="search-results-container">
            {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_LENGTH && (
              <div className="search-hint">
                {t('sparePartsForm.minSearchChars', { count: MIN_SEARCH_LENGTH })}
              </div>
            )}

            {loading && <div className="loading">{t('app.loading')}</div>}

            {error && <div className="error-message">{error}</div>}

            {!loading && !error && searchQuery.length === 0 && (
              <div className="search-hint">
                {t('sparePartsForm.typeToSearch')}
              </div>
            )}

            {!loading && !error && hasSearched && (
              <div className="selection-list">
                {spareParts.map((part) => (
                  <div
                    key={part.id}
                    className="selection-item"
                    onClick={() => handleSelect(part)}
                  >
                    <div className="item-main">{part.partNumber}</div>
                    <div className="item-details">{part.descriptionIt || part.descriptionEn}</div>
                    {part.unit !== undefined && part.unit !== null && (
                      <div className="item-details">{part.unit}</div>
                    )}
                  </div>
                ))}
                {spareParts.length === 0 && (
                  <div className="no-results">{t('mainForm.noResults')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SparePartSelectionModal;
